<template>
  <Toast />
  <div class="card m-4">
    <div class="col-span-12">
      <div class="col-span-6 ml-4">
        <label for="nombre-name" class="block text-sm font-medium text-gray-700">Empresa/Afiliado</label>
        <InputSwitch inputId="nombre-name" v-model="data.save.esEmpresa" @change="cambioEmpresa" />
        <span class="text-xs ml-2">{{ textEsEmpresa }}</span>
      </div>

      <div class="col-span-1 mt-4">
        <label for="CodigoBarra">Codigo de Barra o N&ordm; Comprobante</label>
        <InputText
          id="CodigoBarra"
          v-model.trim="data.save.codBarra"
          type="text"
          class="w-full"
          maxlength="50"
          @change="completarInfo"
        />
      </div>

      <br />

      <div class="flex mb-4 gap-2">
        <label for="cuitData" class="p-3 text-center">CUIT:</label>
        <InputText v-model="cuit" class="w-1/4 text-center" id="cuitData" type="text" disabled />
        <InputText v-model="nombre" class="w-1/2 text-center" id="nombreData" type="text" disabled />
      </div>

      <div v-if="!canGuardarNotaCredito" class="p-3 mb-4 border-round bg-yellow-50 text-yellow-900 border-1 border-yellow-300">
        No puede crear la nota de credito porque no tiene asignado el rol `Iosep Salud - Nota de Credito`.
      </div>

      <div v-show="data.load">
        <DataTable
          :value="dataModel"
          editMode="cell"
          @cell-edit-complete="onCellEditComplete"
          tableClass="editable-cells-table"
        >
          <template #header>
            <div class="sm:flex flex-wrap">
              <div class="sm:flex-grow h-16">
                <Button
                  type="button"
                  label="Actualizar datos"
                  icon="pi pi-refresh"
                  class="p-button-text p-button-sm"
                  @click="completarInfo"
                />
              </div>
            </div>
          </template>

          <Column field="periodo" header="Periodo" style="width: 15%" />
          <Column field="comprobante" header="Comprobante" style="width: 20%" />
          <Column field="tipoComprobante" header="Detalle" style="width: 25%" />
          <Column field="importe" header="Importe" style="width: 20%">
            <template #body="{ data: row }">
              {{ formatCurrency(row.importe) }}
            </template>
          </Column>
          <Column field="pagado" header="Credito de..." style="width: 20%">
            <template #body="{ data: row }">
              {{ formatCurrency(row.pagado || 0) }}
            </template>
            <template #editor="{ data: row, field }">
              <InputNumber
                v-model="row[field]"
                mode="decimal"
                :min="0"
                :max="Number(row.importe || 0)"
                :minFractionDigits="0"
                :maxFractionDigits="2"
                inputClass="w-full"
                autofocus
              />
            </template>
          </Column>

          <ColumnGroup type="footer">
            <Row>
              <Column footer="Total:" :colspan="4" footerStyle="text-align:right" />
              <Column :footer="saldoTotal" />
            </Row>
          </ColumnGroup>
        </DataTable>

        <br />

        <label for="Observaciones">Observacion:</label>
        <Textarea
          v-model.trim="valueTextArea"
          :autoResize="true"
          rows="6"
          class="w-1/2 p-4"
          id="Observaciones"
        />

        <br />
        <br />

        <div class="w-full text-center">
          <Button
            type="button"
            label="Grabar nota de Credito"
            icon="pi pi-save"
            @click="Confirm"
            class="w-1/4"
            :disabled="!canGuardarNotaCredito || data.saving"
          />
          <Button
            type="button"
            label="Cancelar"
            icon="pi pi-times"
            class="p-button-danger w-1/8 ml-2"
            @click="cleanCtaCte"
          />
        </div>
      </div>

      <br />
    </div>
  </div>

  <Toast position="bottom-center" group="bc">
    <template #message="slotProps">
      <div class="w-full">
        <div class="w-full">
          <i class="pi pi-exclamation-triangle" style="font-size: 5rem"></i>
          <h4>{{ slotProps.message.summary }}</h4>
          <p>{{ slotProps.message.detail }}</p>
        </div>
        <br />
        <div class="w-full">
          <div class="w-full">
            <Button class="p-button-success" label="Si" @click="onConfirm" style="width: 100%; margin-bottom: .5rem;" />
            <Button class="p-button-secondary" label="No" @click="onReject" style="width: 100%; margin-bottom: .5rem;" />
          </div>
        </div>
      </div>
    </template>
  </Toast>
</template>

<script>
import { computed, reactive, ref } from 'vue';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Row from 'primevue/row';
import ColumnGroup from 'primevue/columngroup';
import InputNumber from 'primevue/inputnumber';
import InputSwitch from 'primevue/inputswitch';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import WService from '@/plugins/ws';
import { getUserRoles, hasAnyRequiredRole } from '@/services/authRoles';

const REQUIRED_NOTE_CREDIT_ROLES = ['Iosep Salud - Nota de Credito'];

const parseAmount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export default {
  components: {
    InputText,
    Textarea,
    Button,
    DataTable,
    Column,
    InputNumber,
    InputSwitch,
    Toast,
    Row,
    ColumnGroup,
  },
  setup() {
    const toast = useToast();
    const wService = new WService();
    const dataModel = ref([]);
    const cuit = ref('');
    const nombre = ref('');
    const valueTextArea = ref('');
    const userRoles = ref(getUserRoles());

    const data = reactive({
      save: {
        esEmpresa: false,
        codBarra: '',
      },
      load: false,
      saving: false,
    });

    const textEsEmpresa = computed(() => (data.save.esEmpresa ? 'Empresa' : 'Afiliado'));
    const tipo = computed(() => (data.save.esEmpresa ? 0 : 1));
    const canGuardarNotaCredito = computed(() =>
      hasAnyRequiredRole(REQUIRED_NOTE_CREDIT_ROLES, userRoles.value)
    );

    const saldoTotal = computed(() => {
      const total = dataModel.value.reduce((sum, item) => sum + parseAmount(item.importe), 0);
      return formatCurrency(total);
    });

    const totalPagado = computed(() => {
      return dataModel.value.reduce((sum, item) => sum + parseAmount(item.pagado), 0);
    });

    const showRoleRequiredMessage = () => {
      toast.add({
        severity: 'warn',
        summary: 'Permisos insuficientes',
        detail: 'No puede crear la nota de credito porque no tiene asignado el rol Iosep Salud - Nota de Credito.',
        life: 5000,
      });
    };

    const cleanCtaCte = () => {
      cuit.value = '';
      nombre.value = '';
      valueTextArea.value = '';
      data.save.codBarra = '';
      dataModel.value = [];
      data.load = false;
      data.saving = false;
    };

    const normalizarRows = (rows) => {
      return rows.map((item) => ({
        ...item,
        importe: parseAmount(item.importe),
        pagado: parseAmount(item.pagado),
      }));
    };

    const completarInfo = async () => {
      const codBarra = (data.save.codBarra || '').trim();
      if (!codBarra) {
        cleanCtaCte();
        toast.add({
          severity: 'error',
          summary: 'Mensaje de error',
          detail: 'Debe ingresar un codigo de barra o comprobante',
          life: 3000,
        });
        return;
      }

      cuit.value = '';
      nombre.value = '';
      dataModel.value = [];
      data.load = false;

      try {
        const encodedCodBarra = encodeURIComponent(codBarra);
        const info = data.save.esEmpresa
          ? await wService.postMethod(`Comprobantes/NotaCreditoEmpresa/${encodedCodBarra}`, {})
          : await wService.getMethod(`Comprobantes/NotaCredito/${encodedCodBarra}`);

        if (!Array.isArray(info) || info.length === 0) {
          toast.add({
            severity: 'error',
            summary: 'Mensaje de error',
            detail: data.save.esEmpresa ? 'Cuenta empresa no encontrada' : 'Afiliado no encontrado',
            life: 3000,
          });
          return;
        }

        const rows = normalizarRows(info);
        dataModel.value = rows;
        cuit.value = rows[0].cuil || '';
        nombre.value = rows[0].nombre || '';
        data.load = true;
      } catch (error) {
        console.log(error?.message || error);
        toast.add({
          severity: 'error',
          summary: 'Mensaje de error',
          detail: 'No se pudo recuperar la informacion para generar la nota de credito',
          life: 4000,
        });
      }
    };

    const cambioEmpresa = () => {
      cleanCtaCte();
    };

    const formatCurrency = (value) => {
      const amount = parseAmount(value);
      return amount.toLocaleString('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    const Confirm = () => {
      if (!canGuardarNotaCredito.value) {
        showRoleRequiredMessage();
        return;
      }

      if (!cuit.value) {
        toast.add({
          severity: 'error',
          summary: 'Mensaje de error',
          detail: 'No hay un afiliado o empresa cargado',
          life: 3000,
        });
        return;
      }

      if (!valueTextArea.value) {
        toast.add({
          severity: 'error',
          summary: 'Mensaje de error',
          detail: 'Debe colocar una observacion',
          life: 3000,
        });
        return;
      }

      if (totalPagado.value <= 0) {
        toast.add({
          severity: 'error',
          summary: 'Mensaje de error',
          detail: 'Debe colocar un importe de credito mayor a cero',
          life: 3000,
        });
        return;
      }

      toast.add({
        severity: 'warn',
        summary: 'Esta seguro?',
        detail: 'Si confirma los cambios seran permanentes',
        group: 'bc',
      });
    };

    const buildRows = () => {
      return dataModel.value
        .filter((item) => parseAmount(item.pagado) > 0)
        .map((item) => ({
          IdDetalle: Number(item.idDetalle || 0),
          Periodo: String(item.periodo || ''),
          IdComprobante: String(item.comprobante || item.idComprobante || ''),
          Importe: parseAmount(item.importe),
          Pagado: parseAmount(item.pagado).toString(),
        }));
    };

    const onConfirm = async () => {
      if (!canGuardarNotaCredito.value) {
        toast.removeGroup('bc');
        showRoleRequiredMessage();
        return;
      }

      if (data.saving) {
        return;
      }

      const rows = buildRows();
      if (!rows.length) {
        toast.removeGroup('bc');
        toast.add({
          severity: 'error',
          summary: 'Mensaje de error',
          detail: 'No hay importes para guardar',
          life: 3000,
        });
        return;
      }

      data.saving = true;

      try {
        const encodedCodBarra = encodeURIComponent((data.save.codBarra || '').trim());
        const encodedObservaciones = encodeURIComponent(valueTextArea.value.trim());
        const url = `Comprobantes/GuardarNotaCredito/${encodedCodBarra}/${tipo.value}/${encodedObservaciones}`;
        const body = { rows };
        const info = await wService.postMethod(url, body);

        if (info !== 'error') {
          toast.add({
            severity: 'success',
            summary: 'Mensaje exitoso',
            detail: 'Nota de credito guardada',
            life: 5000,
          });
          toast.removeGroup('bc');
          cleanCtaCte();
          return;
        }

        toast.add({
          severity: 'error',
          summary: 'Mensaje de error',
          detail: 'Error al guardar la nota de credito',
          life: 5000,
        });
      } catch (error) {
        console.log(error?.message || error);

        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          toast.add({
            severity: 'warn',
            summary: 'Permisos insuficientes',
            detail: 'No puede crear la nota de credito porque no tiene asignado el rol Iosep Salud - Nota de Credito.',
            life: 5000,
          });
        } else {
          toast.add({
            severity: 'error',
            summary: 'Mensaje de error',
            detail: 'Ocurrio un error al guardar la nota de credito',
            life: 5000,
          });
        }
      } finally {
        data.saving = false;
        toast.removeGroup('bc');
      }
    };

    const onReject = () => {
      toast.removeGroup('bc');
    };

    const onCellEditComplete = (event) => {
      const { data: row, newValue, field } = event;

      if (field !== 'pagado') {
        return;
      }

      const amount = parseAmount(newValue);
      const importe = parseAmount(row.importe);

      if (amount < 0 || amount > importe) {
        event.preventDefault();
        toast.add({
          severity: 'error',
          summary: 'Mensaje de error',
          detail: 'El importe a acreditar debe estar entre 0 y el importe del comprobante',
          life: 3000,
        });
        return;
      }

      row[field] = amount;
    };

    return {
      data,
      dataModel,
      nombre,
      valueTextArea,
      cuit,
      textEsEmpresa,
      saldoTotal,
      canGuardarNotaCredito,
      Confirm,
      completarInfo,
      cambioEmpresa,
      onConfirm,
      onReject,
      onCellEditComplete,
      formatCurrency,
      cleanCtaCte,
    };
  },
};
</script>
